/**
 * @file index.tsx
 *
 * @fileoverview Renders a block fixed on the side to display aditional navigation.
 */

import { cva, VariantProps } from "class-variance-authority";
import React, { useMemo } from "react";
import { useScrollYPosition } from "../../../../hooks/useScrollPosition";
import ArrowLeftIcon from "@jq-icons/ArrowLeft";
import ArrowRightIcon from "@jq-icons/ArrowRight";
import Button from "../../../02-atoms/01-forms/01-button";
import { FIXED_SCROLLING_SHOW_HEADER } from "../../../03-molecules/03-layout/02-header-fixed";
import styles from "./sideBlock.module.scss";

export const sideBlock = cva([styles.sideBlock], {
  variants: {
    collapsed: {
      true: styles.sideBlockCollapsed,
    },
  },
});
export const sideBlockHead = cva([styles.sideBlockHead], {
  variants: {
    collapsed: {
      true: styles.sideBlockHeadCollapsed,
    },
  },
});

export type SideMenuProps = VariantProps<typeof sideBlock> & {
  /**
   * On collapse.
   */
  onCollapse?: () => void;
  /**
   * The content to show inside the side block.
   */
  children: React.ReactNode;
  /**
   * Content to show on top of the block.
   */
  blockHead?: React.ReactNode;
};

/**
 * A side block to show some sort of navigation. This block is fixed
 * so should be used mostly for roles and places where desktop viewing is mostly the
 * recommendation.
 */
export const SideBlock = ({
  children,
  collapsed,
  onCollapse,
  blockHead,
}: SideMenuProps) => {
  const scrollY = useScrollYPosition();

  const fixedHeaderHeight = useMemo(() => {
    /**
     * When the user scrolls below the breakpoint define in the theme,
     * we want to grab the reference to the fixed header and add padding
     * to the dashboard so it is not overflow by the header.
     */
    if (scrollY > FIXED_SCROLLING_SHOW_HEADER) {
      const navbar = document.getElementById("fixed-navbar");
      return navbar?.offsetHeight ?? 0;
    } else return 0;
  }, [scrollY]);

  return (
    <aside
      className={sideBlock({ collapsed })}
      style={{
        paddingTop: `${fixedHeaderHeight}px`,
      }}
    >
      {blockHead && (
        <div className={sideBlockHead({ collapsed })}>{blockHead}</div>
      )}
      {children}
      <div
        className={styles.toggleWrapper}
        style={{
          marginBottom: `${fixedHeaderHeight}px`,
        }}
      >
        <Button
          raised={false}
          iconed={true}
          onClick={(e) => {
            onCollapse && onCollapse();
          }}
        >
          {collapsed ? (
            <ArrowRightIcon title="Open" />
          ) : (
            <ArrowLeftIcon title="collapse" />
          )}
        </Button>
      </div>
    </aside>
  );
};
export default SideBlock;
